import { useState, useCallback, useRef, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { globalKnowledgeService, GlobalCategory, GlobalDocument, GlobalDomain } from '@/services/api/global-knowledge';
import { useDropzone } from 'react-dropzone';
import { useAuthStore } from '@/store/authStore';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import {
  Card,
  CardContent,
} from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { Textarea } from '@/components/ui/textarea';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import { ScrollArea } from '@/components/ui/scroll-area';
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from '@/components/ui/tabs';
import {
  Upload,
  Search,
  MoreHorizontal,
  Download,
  Trash2,
  RefreshCw,
  FileText,
  FileImage,
  FileSpreadsheet,
  File,
  CheckCircle2,
  XCircle,
  Clock,
  Loader2,
  Plus,
  Pencil,
  FolderOpen,
  Library,
  X,
  AlertCircle,
  Scale,
  Calculator,
  Briefcase,
  Cog,
  GraduationCap,
  Heart,
  Building,
  Globe,
  Landmark,
  Users,
  ChevronLeft,
  ChevronRight,
  List,
} from 'lucide-react';
import { formatDistanceToNow } from 'date-fns';

// Category icons mapping
const CATEGORY_ICONS: Record<string, React.ElementType> = {
  accounting: Calculator,
  tax: Landmark,
  legal: Scale,
  engineering: Cog,
  hr: Users,
  marketing: Globe,
  finance: Briefcase,
  health: Heart,
  education: GraduationCap,
  general: Building,
  default: FolderOpen,
};

function getCategoryIcon(slug: string) {
  return CATEGORY_ICONS[slug.toLowerCase()] || CATEGORY_ICONS.default;
}

const FILE_ICONS: Record<string, React.ElementType> = {
  pdf: FileText,
  doc: FileText,
  docx: FileText,
  txt: FileText,
  csv: FileSpreadsheet,
  xlsx: FileSpreadsheet,
  xls: FileSpreadsheet,
  png: FileImage,
  jpg: FileImage,
  jpeg: FileImage,
  gif: FileImage,
};

function getFileIcon(fileType: string) {
  return FILE_ICONS[fileType.toLowerCase()] || File;
}

// Component to render chunk content with table support
function ChunkContent({ text }: { text: string }) {
  // Parse text and split into table/non-table sections
  const parts: Array<{ type: 'text' | 'table'; content: string }> = [];
  let remaining = text;

  while (remaining.length > 0) {
    const tableStart = remaining.indexOf('[TABLE]');
    if (tableStart === -1) {
      // No more tables, add remaining text
      if (remaining.trim()) {
        parts.push({ type: 'text', content: remaining });
      }
      break;
    }

    // Add text before table
    if (tableStart > 0) {
      const beforeTable = remaining.substring(0, tableStart);
      if (beforeTable.trim()) {
        parts.push({ type: 'text', content: beforeTable });
      }
    }

    // Find table end
    const tableEnd = remaining.indexOf('[/TABLE]', tableStart);
    if (tableEnd === -1) {
      // No closing tag, treat rest as text
      parts.push({ type: 'text', content: remaining.substring(tableStart) });
      break;
    }

    // Extract table content
    const tableContent = remaining.substring(tableStart + 7, tableEnd);
    parts.push({ type: 'table', content: tableContent });

    // Move past the table
    remaining = remaining.substring(tableEnd + 8);
  }

  if (parts.length === 0) {
    return (
      <p className="text-sm whitespace-pre-wrap break-words font-mono bg-muted/50 p-3 rounded">
        {text}
      </p>
    );
  }

  return (
    <div className="space-y-3">
      {parts.map((part, idx) => {
        if (part.type === 'text') {
          return (
            <p key={idx} className="text-sm whitespace-pre-wrap break-words font-mono bg-muted/50 p-3 rounded">
              {part.content.trim()}
            </p>
          );
        }

        // Parse table: rows separated by newlines, cells by pipes
        const rows = part.content.trim().split('\n').filter(row => row.trim());
        if (rows.length === 0) return null;

        return (
          <div key={idx} className="overflow-x-auto">
            <table className="w-full border-collapse text-sm">
              <tbody>
                {rows.map((row, rowIdx) => {
                  const cells = row.split('|').map(cell => cell.trim()).filter(cell => cell);
                  return (
                    <tr key={rowIdx} className={rowIdx === 0 ? 'bg-muted/70 font-medium' : ''}>
                      {cells.map((cell, cellIdx) => (
                        <td
                          key={cellIdx}
                          className="border border-border px-3 py-2 text-left"
                        >
                          {cell}
                        </td>
                      ))}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        );
      })}
    </div>
  );
}

function formatFileSize(bytes: number): string {
  if (bytes === 0) return '0 B';
  const k = 1024;
  const sizes = ['B', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return `${parseFloat((bytes / Math.pow(k, i)).toFixed(1))} ${sizes[i]}`;
}

interface StatusBadgeProps {
  status: GlobalDocument['status'];
}

function StatusBadge({ status }: StatusBadgeProps) {
  switch (status) {
    case 'indexed':
      return (
        <Badge variant="default" className="bg-green-500">
          <CheckCircle2 className="mr-1 h-3 w-3" />
          Indexed
        </Badge>
      );
    case 'indexing':
      return (
        <Badge variant="secondary">
          <Loader2 className="mr-1 h-3 w-3 animate-spin" />
          Indexing
        </Badge>
      );
    case 'uploaded':
      return (
        <Badge variant="outline" className="border-blue-500 text-blue-600">
          <Upload className="mr-1 h-3 w-3" />
          Uploaded
        </Badge>
      );
    case 'failed':
      return (
        <Badge variant="destructive">
          <XCircle className="mr-1 h-3 w-3" />
          Failed
        </Badge>
      );
    case 'pending':
    default:
      return (
        <Badge variant="outline">
          <Clock className="mr-1 h-3 w-3" />
          Pending
        </Badge>
      );
  }
}

// File upload types
const ACCEPTED_TYPES = {
  'application/pdf': ['.pdf'],
  'text/plain': ['.txt'],
  'text/csv': ['.csv'],
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document': ['.docx'],
  'application/msword': ['.doc'],
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': ['.xlsx'],
  'application/vnd.ms-excel': ['.xls'],
};

const MAX_FILE_SIZE = 50 * 1024 * 1024; // 50MB

const EXTENSION_TO_MIME: Record<string, string> = {
  '.pdf': 'application/pdf',
  '.txt': 'text/plain',
  '.csv': 'text/csv',
  '.docx': 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  '.doc': 'application/msword',
  '.xlsx': 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  '.xls': 'application/vnd.ms-excel',
};

function getContentType(file: File): string {
  if (file.type) return file.type;
  const ext = '.' + file.name.split('.').pop()?.toLowerCase();
  return EXTENSION_TO_MIME[ext] || 'application/octet-stream';
}

interface UploadingFile {
  file: File;
  progress: number;
  status: 'pending' | 'uploading' | 'confirming' | 'processing' | 'success' | 'error';
  error?: string;
  documentId?: string;
}

export function GlobalKnowledgePage() {
  const { isSuperAdmin } = useAuthStore();
  const isAdmin = isSuperAdmin();
  const queryClient = useQueryClient();
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState<string>('');
  const [currentPage, setCurrentPage] = useState(1);
  const PAGE_SIZE = 20;
  const [deleteDocDialogOpen, setDeleteDocDialogOpen] = useState(false);
  const [deleteCategoryDialogOpen, setDeleteCategoryDialogOpen] = useState(false);
  const [selectedDocument, setSelectedDocument] = useState<GlobalDocument | null>(null);
  const [selectedCategoryToDelete, setSelectedCategoryToDelete] = useState<GlobalCategory | null>(null);

  // Category form state
  const [categoryDialogOpen, setCategoryDialogOpen] = useState(false);
  const [editingCategory, setEditingCategory] = useState<GlobalCategory | null>(null);
  const [categoryForm, setCategoryForm] = useState({
    name: '',
    slug: '',
    description: '',
    icon: 'default',
    domainId: '' as string,
  });

  // Domain form state
  const [domainDialogOpen, setDomainDialogOpen] = useState(false);
  const [editingDomain, setEditingDomain] = useState<GlobalDomain | null>(null);
  const [domainForm, setDomainForm] = useState({
    name: '',
    slug: '',
    description: '',
    icon: 'default',
  });
  const [deleteDomainDialogOpen, setDeleteDomainDialogOpen] = useState(false);
  const [selectedDomainToDelete, setSelectedDomainToDelete] = useState<GlobalDomain | null>(null);
  const [deleteAllDialogOpen, setDeleteAllDialogOpen] = useState(false);
  const [viewChunksDialogOpen, setViewChunksDialogOpen] = useState(false);
  const [viewChunksDoc, setViewChunksDoc] = useState<GlobalDocument | null>(null);
  const [chunksData, setChunksData] = useState<{
    documentId: string;
    filename: string;
    mainTopic?: string;
    totalChunks: number;
    chunks: Array<{ index: number; text: string; tokens?: number }>;
  } | null>(null);
  const [loadingChunks, setLoadingChunks] = useState(false);

  // Upload form state
  const [files, setFiles] = useState<UploadingFile[]>([]);
  const [isUploading, setIsUploading] = useState(false);

  // Fetch domains
  const { data: domainsData, isLoading: loadingDomains } = useQuery({
    queryKey: ['global-domains'],
    queryFn: () => globalKnowledgeService.listDomains(),
  });

  // Fetch categories
  const { data: categoriesData, isLoading: loadingCategories } = useQuery({
    queryKey: ['global-categories'],
    queryFn: () => globalKnowledgeService.listCategories(),
  });

  // Fetch documents for selected category with pagination
  const { data: documentsData, isLoading: loadingDocuments, refetch: refetchDocuments } = useQuery({
    queryKey: ['global-documents', selectedCategory, searchQuery, currentPage],
    queryFn: () => globalKnowledgeService.listDocuments({
      categoryId: selectedCategory || undefined,
      search: searchQuery || undefined,
      limit: PAGE_SIZE,
      offset: (currentPage - 1) * PAGE_SIZE,
    }),
    enabled: true,
  });

  // Domain mutations
  const createDomainMutation = useMutation({
    mutationFn: (data: { name: string; slug: string; description?: string; icon?: string }) =>
      globalKnowledgeService.createDomain(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['global-domains'] });
      closeDomainDialog();
    },
  });

  const updateDomainMutation = useMutation({
    mutationFn: ({ id, data }: { id: string; data: { name?: string; description?: string; icon?: string } }) =>
      globalKnowledgeService.updateDomain(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['global-domains'] });
      closeDomainDialog();
    },
  });

  const deleteDomainMutation = useMutation({
    mutationFn: (id: string) => globalKnowledgeService.deleteDomain(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['global-domains'] });
      queryClient.invalidateQueries({ queryKey: ['global-categories'] });
      setDeleteDomainDialogOpen(false);
      setSelectedDomainToDelete(null);
    },
  });

  // Create category mutation
  const createCategoryMutation = useMutation({
    mutationFn: (data: { name: string; slug: string; description?: string; icon?: string; domainId?: string }) =>
      globalKnowledgeService.createCategory(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['global-categories'] });
      closeCategoryDialog();
    },
  });

  // Update category mutation
  const updateCategoryMutation = useMutation({
    mutationFn: ({ id, data }: { id: string; data: { name?: string; description?: string; icon?: string; domainId?: string } }) =>
      globalKnowledgeService.updateCategory(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['global-categories'] });
      closeCategoryDialog();
    },
  });

  // Delete category mutation
  const deleteCategoryMutation = useMutation({
    mutationFn: (id: string) => globalKnowledgeService.deleteCategory(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['global-categories'] });
      queryClient.invalidateQueries({ queryKey: ['global-documents'] });
      if (selectedCategory === selectedCategoryToDelete?.id) {
        setSelectedCategory('');
      }
      setDeleteCategoryDialogOpen(false);
      setSelectedCategoryToDelete(null);
    },
  });

  // Delete document mutation
  const deleteDocMutation = useMutation({
    mutationFn: (id: string) => globalKnowledgeService.deleteDocument(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['global-documents'] });
      queryClient.invalidateQueries({ queryKey: ['global-categories'] });
      setDeleteDocDialogOpen(false);
      setSelectedDocument(null);
    },
  });

  // Delete ALL documents mutation
  const deleteAllMutation = useMutation({
    mutationFn: (categoryId?: string) => globalKnowledgeService.deleteAllDocuments(categoryId),
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['global-documents'] });
      queryClient.invalidateQueries({ queryKey: ['global-categories'] });
      setDeleteAllDialogOpen(false);
      toast.success(`Deleted ${data.deletedCount} documents`);
    },
    onError: () => {
      toast.error('Failed to delete documents');
    },
  });

  // Track which specific document is being reindexed
  const [reindexingDocId, setReindexingDocId] = useState<string | null>(null);

  // Re-index document mutation with polling for completion
  const reindexDocMutation = useMutation({
    mutationFn: async (id: string) => {
      setReindexingDocId(id);
      await globalKnowledgeService.reprocessDocument(id);

      // Poll for completion (async processing takes 10-30 seconds)
      const maxAttempts = 30; // 30 attempts x 2 seconds = 60 seconds max
      for (let attempt = 0; attempt < maxAttempts; attempt++) {
        await new Promise(resolve => setTimeout(resolve, 2000));

        try {
          const doc = await globalKnowledgeService.getDocument(id);

          if (doc.status === 'indexed') {
            return { success: true, status: 'indexed' };
          }
          if (doc.status === 'failed') {
            throw new Error('Document processing failed');
          }

          // Still processing - refresh the list to show progress
          queryClient.invalidateQueries({ queryKey: ['global-documents'] });
        } catch (fetchError: any) {
          if (fetchError.message === 'Document processing failed') throw fetchError;
          console.error('Error fetching document status:', fetchError);
        }
      }

      throw new Error('Processing timeout - please check document status');
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['global-documents'] });
      setReindexingDocId(null);
    },
    onError: () => {
      queryClient.invalidateQueries({ queryKey: ['global-documents'] });
      setReindexingDocId(null);
    },
  });

  // Download document handler
  const handleDownload = async (doc: GlobalDocument) => {
    try {
      const { downloadUrl } = await globalKnowledgeService.getDownloadUrl(doc.id);
      window.open(downloadUrl, '_blank');
    } catch (error) {
      console.error('Failed to get download URL:', error);
    }
  };

  // Re-index document handler
  const handleReindex = (doc: GlobalDocument) => {
    reindexDocMutation.mutate(doc.id);
  };

  // View chunks handler
  const handleViewChunks = async (doc: GlobalDocument) => {
    setViewChunksDoc(doc);
    setViewChunksDialogOpen(true);
    setLoadingChunks(true);
    setChunksData(null);
    try {
      const data = await globalKnowledgeService.getDocumentChunks(doc.id);
      setChunksData(data);
    } catch (error) {
      console.error('Failed to fetch chunks:', error);
    } finally {
      setLoadingChunks(false);
    }
  };

  // File dropzone
  const onDrop = useCallback((acceptedFiles: File[]) => {
    const newFiles = acceptedFiles.map((file) => ({
      file,
      progress: 0,
      status: 'pending' as const,
    }));
    setFiles((prev) => [...prev, ...newFiles]);
  }, []);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: ACCEPTED_TYPES,
    maxSize: MAX_FILE_SIZE,
    multiple: true,
  });

  const removeFile = (index: number) => {
    setFiles((prev) => prev.filter((_, i) => i !== index));
  };

  const uploadFiles = async () => {
    if (files.length === 0 || !selectedCategory) return;

    setIsUploading(true);

    for (let i = 0; i < files.length; i++) {
      const uploadFile = files[i];
      if (uploadFile.status !== 'pending') continue;

      try {
        setFiles((prev) =>
          prev.map((f, idx) =>
            idx === i ? { ...f, status: 'uploading' as const, progress: 10 } : f
          )
        );

        const contentType = getContentType(uploadFile.file);
        const uploadData = await globalKnowledgeService.getUploadUrl(
          selectedCategory,
          uploadFile.file.name,
          contentType,
          uploadFile.file.size
        );

        setFiles((prev) =>
          prev.map((f, idx) =>
            idx === i ? { ...f, progress: 30, documentId: uploadData.documentId } : f
          )
        );

        await globalKnowledgeService.uploadFile(uploadData.uploadUrl, uploadFile.file, contentType);

        setFiles((prev) =>
          prev.map((f, idx) =>
            idx === i ? { ...f, progress: 70, status: 'confirming' as const } : f
          )
        );

        await globalKnowledgeService.confirmUpload(uploadData.documentId);

        setFiles((prev) =>
          prev.map((f, idx) =>
            idx === i ? { ...f, progress: 80, status: 'processing' as const } : f
          )
        );

        // Fire off processing (may 504 due to API Gateway 29s limit, but Lambda continues)
        try {
          await globalKnowledgeService.reprocessDocument(uploadData.documentId);
        } catch {
          // 504 expected for large files - Lambda keeps running
        }

        // Poll for completion
        let indexed = false;
        for (let attempt = 0; attempt < 60; attempt++) {
          await new Promise(resolve => setTimeout(resolve, 3000));
          try {
            const doc = await globalKnowledgeService.getDocument(uploadData.documentId);
            if (doc.status === 'indexed') {
              indexed = true;
              break;
            }
            if (doc.status === 'failed') {
              throw new Error('Document processing failed');
            }
          } catch (pollErr: any) {
            if (pollErr.message === 'Document processing failed') throw pollErr;
          }
        }

        if (indexed) {
          setFiles((prev) =>
            prev.map((f, idx) =>
              idx === i ? { ...f, progress: 100, status: 'success' as const } : f
            )
          );
        } else {
          setFiles((prev) =>
            prev.map((f, idx) =>
              idx === i ? { ...f, status: 'error' as const, error: 'Processing timeout' } : f
            )
          );
        }
      } catch (error: any) {
        setFiles((prev) =>
          prev.map((f, idx) =>
            idx === i
              ? { ...f, status: 'error' as const, error: error.message || 'Upload failed' }
              : f
          )
        );
      }
    }

    setIsUploading(false);
    queryClient.invalidateQueries({ queryKey: ['global-documents'] });
    queryClient.invalidateQueries({ queryKey: ['global-categories'] });

    // Clear successful uploads after a delay
    setTimeout(() => {
      setFiles((prev) => prev.filter((f) => f.status !== 'success'));
    }, 2000);
  };

  // Domain form handlers
  const openCreateDomainDialog = () => {
    setEditingDomain(null);
    setDomainForm({ name: '', slug: '', description: '', icon: 'default' });
    setDomainDialogOpen(true);
  };

  const openEditDomainDialog = (domain: GlobalDomain) => {
    setEditingDomain(domain);
    setDomainForm({
      name: domain.name,
      slug: domain.slug,
      description: domain.description || '',
      icon: domain.icon || 'default',
    });
    setDomainDialogOpen(true);
  };

  const closeDomainDialog = () => {
    setDomainDialogOpen(false);
    setEditingDomain(null);
    setDomainForm({ name: '', slug: '', description: '', icon: 'default' });
  };

  const handleDomainSubmit = () => {
    if (editingDomain) {
      updateDomainMutation.mutate({
        id: editingDomain.id,
        data: { name: domainForm.name, description: domainForm.description, icon: domainForm.icon },
      });
    } else {
      createDomainMutation.mutate({
        name: domainForm.name,
        slug: domainForm.slug || domainForm.name.toLowerCase().replace(/\s+/g, '-'),
        description: domainForm.description,
        icon: domainForm.icon,
      });
    }
  };

  const handleDeleteDomain = (domain: GlobalDomain) => {
    setSelectedDomainToDelete(domain);
    setDeleteDomainDialogOpen(true);
  };

  // Category form handlers
  const openCreateCategoryDialog = () => {
    setEditingCategory(null);
    setCategoryForm({ name: '', slug: '', description: '', icon: 'default', domainId: '' });
    setCategoryDialogOpen(true);
  };

  const openEditCategoryDialog = (category: GlobalCategory) => {
    setEditingCategory(category);
    setCategoryForm({
      name: category.name,
      slug: category.slug,
      description: category.description || '',
      icon: category.icon || 'default',
      domainId: category.domainId || '',
    });
    setCategoryDialogOpen(true);
  };

  const closeCategoryDialog = () => {
    setCategoryDialogOpen(false);
    setEditingCategory(null);
    setCategoryForm({ name: '', slug: '', description: '', icon: 'default', domainId: '' });
  };

  const handleCategorySubmit = () => {
    if (editingCategory) {
      updateCategoryMutation.mutate({
        id: editingCategory.id,
        data: {
          name: categoryForm.name,
          description: categoryForm.description,
          icon: categoryForm.icon,
          domainId: categoryForm.domainId || undefined,
        },
      });
    } else {
      createCategoryMutation.mutate({
        name: categoryForm.name,
        slug: categoryForm.slug || categoryForm.name.toLowerCase().replace(/\s+/g, '-'),
        description: categoryForm.description,
        icon: categoryForm.icon,
        domainId: categoryForm.domainId || undefined,
      });
    }
  };

  const handleSearch = useCallback((e: React.FormEvent) => {
    e.preventDefault();
    setCurrentPage(1);
    refetchDocuments();
  }, [refetchDocuments]);

  const handleDeleteDocument = (doc: GlobalDocument) => {
    setSelectedDocument(doc);
    setDeleteDocDialogOpen(true);
  };

  const handleDeleteCategory = (category: GlobalCategory) => {
    setSelectedCategoryToDelete(category);
    setDeleteCategoryDialogOpen(true);
  };

  // Upload dialog state
  const [uploadDialogOpen, setUploadDialogOpen] = useState(false);
  const [uploadCategoryId, setUploadCategoryId] = useState<string>('');
  const [dialogFiles, setDialogFiles] = useState<UploadingFile[]>([]);
  const [isDialogUploading, setIsDialogUploading] = useState(false);
  const dialogFileInputRef = useCallback((node: HTMLInputElement | null) => {
    if (node) node.value = '';
  }, []);
  const fileInputRef2 = useCallback((node: HTMLInputElement | null) => {
    // just for ref
  }, []);

  const handleDialogFileSelect = (fileList: FileList | null) => {
    if (!fileList) return;
    const newFiles = Array.from(fileList).map((file) => ({
      file,
      progress: 0,
      status: 'pending' as const,
    }));
    setDialogFiles((prev) => [...prev, ...newFiles]);
  };

  const removeDialogFile = (index: number) => {
    setDialogFiles((prev) => prev.filter((_, i) => i !== index));
  };

  const uploadDialogFiles = async () => {
    if (dialogFiles.length === 0 || !uploadCategoryId) return;
    setIsDialogUploading(true);

    for (let i = 0; i < dialogFiles.length; i++) {
      const uploadFile = dialogFiles[i];
      if (uploadFile.status !== 'pending') continue;

      try {
        setDialogFiles((prev) =>
          prev.map((f, idx) => idx === i ? { ...f, status: 'uploading' as const, progress: 10 } : f)
        );

        const contentType = getContentType(uploadFile.file);
        const uploadData = await globalKnowledgeService.getUploadUrl(
          uploadCategoryId, uploadFile.file.name, contentType, uploadFile.file.size
        );

        setDialogFiles((prev) =>
          prev.map((f, idx) => idx === i ? { ...f, progress: 30, documentId: uploadData.documentId } : f)
        );

        await globalKnowledgeService.uploadFile(uploadData.uploadUrl, uploadFile.file, contentType);

        setDialogFiles((prev) =>
          prev.map((f, idx) => idx === i ? { ...f, progress: 60, status: 'confirming' as const } : f)
        );

        await globalKnowledgeService.confirmUpload(uploadData.documentId);

        setDialogFiles((prev) =>
          prev.map((f, idx) => idx === i ? { ...f, progress: 80, status: 'processing' as const } : f)
        );

        // Fire off processing (may 504 due to API Gateway 29s limit, but Lambda continues)
        try {
          await globalKnowledgeService.reprocessDocument(uploadData.documentId);
        } catch {
          // 504 expected for large files - Lambda keeps running
        }

        // Poll for completion
        let indexed = false;
        for (let attempt = 0; attempt < 60; attempt++) {
          await new Promise(resolve => setTimeout(resolve, 3000));
          try {
            const doc = await globalKnowledgeService.getDocument(uploadData.documentId);
            if (doc.status === 'indexed') {
              indexed = true;
              break;
            }
            if (doc.status === 'failed') {
              throw new Error('Document processing failed');
            }
          } catch (pollErr: any) {
            if (pollErr.message === 'Document processing failed') throw pollErr;
          }
        }

        if (indexed) {
          setDialogFiles((prev) =>
            prev.map((f, idx) => idx === i ? { ...f, progress: 100, status: 'success' as const } : f)
          );
        } else {
          setDialogFiles((prev) =>
            prev.map((f, idx) => idx === i ? { ...f, status: 'error' as const, error: 'Processing timeout' } : f)
          );
        }
      } catch (error: any) {
        setDialogFiles((prev) =>
          prev.map((f, idx) => idx === i ? { ...f, status: 'error' as const, error: error.message || 'Upload failed' } : f)
        );
      }
    }

    setIsDialogUploading(false);
    queryClient.invalidateQueries({ queryKey: ['global-documents'] });
    queryClient.invalidateQueries({ queryKey: ['global-categories'] });
  };

  const closeUploadDialog = () => {
    if (isDialogUploading) return;
    setUploadDialogOpen(false);
    setDialogFiles([]);
    setUploadCategoryId('');
  };

  const dialogPendingCount = dialogFiles.filter((f) => f.status === 'pending').length;
  const dialogDoneCount = dialogFiles.filter((f) => f.status === 'success').length;

  const pendingCount = files.filter((f) => f.status === 'pending').length;
  const allDomains = domainsData?.domains || [];
  const categories = categoriesData?.categories || [];
  // Non-admins only see domains that have categories they can access
  const domains = isAdmin ? allDomains : allDomains.filter(d => categories.some(c => c.domainId === d.id));
  const documents = documentsData?.documents || [];
  const totalDocuments = documentsData?.total || 0;
  const totalPages = Math.ceil(totalDocuments / PAGE_SIZE);
  const selectedCategoryData = categories.find((c) => c.id === selectedCategory);

  return (
    <div className="flex flex-col h-[calc(100vh-120px)]">
      {/* Header */}
      <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between mb-4">
        <div>
          <h1 className="text-2xl font-bold tracking-tight flex items-center gap-2">
            <Library className="h-6 w-6" />
            Global Knowledge Base
          </h1>
          <p className="text-sm text-muted-foreground">
            Manage platform-wide knowledge categories and documents for tenant access
          </p>
        </div>
        {isAdmin && (
          <div className="flex items-center gap-2">
            <Button variant="outline" onClick={() => setUploadDialogOpen(true)}>
              <Upload className="mr-2 h-4 w-4" />
              Upload Files
            </Button>
            <Button variant="outline" onClick={openCreateDomainDialog}>
              <Plus className="mr-2 h-4 w-4" />
              New Domain
            </Button>
            <Button onClick={openCreateCategoryDialog}>
              <Plus className="mr-2 h-4 w-4" />
              New Category
            </Button>
          </div>
        )}
      </div>

      {/* Main Content - Two Column Layout */}
      <div className="grid grid-cols-1 lg:grid-cols-[280px_1fr] gap-4 flex-1 min-h-0">
        {/* Categories Sidebar */}
        <Card className="flex flex-col overflow-hidden">
          <div className="p-3 border-b flex-shrink-0">
            <h3 className="font-semibold text-sm">Categories</h3>
          </div>
          <div className="p-2 flex-1 overflow-y-auto">
            {loadingCategories ? (
              <div className="space-y-2">
                {[...Array(4)].map((_, i) => (
                  <Skeleton key={i} className="h-10 w-full" />
                ))}
              </div>
            ) : categories.length === 0 && domains.length === 0 ? (
              <div className="text-center py-6 text-muted-foreground">
                <FolderOpen className="h-8 w-8 mx-auto mb-2 opacity-50" />
                <p className="text-sm">No categories yet</p>
                {isAdmin && (
                  <Button
                    variant="link"
                    size="sm"
                    className="mt-1"
                    onClick={openCreateCategoryDialog}
                  >
                    Create your first category
                  </Button>
                )}
              </div>
            ) : (
              <div className="space-y-1">
                <button
                  onClick={() => { setSelectedCategory(''); setCurrentPage(1); }}
                  className={`w-full flex items-center gap-2 px-3 py-2 rounded-md text-sm transition-colors ${
                    selectedCategory === ''
                      ? 'bg-primary text-primary-foreground'
                      : 'hover:bg-muted'
                  }`}
                >
                  <FolderOpen className="h-4 w-4" />
                  <span className="flex-1 text-left">All Documents</span>
                  <Badge variant="secondary" className="text-xs">
                    {categories.reduce((sum, c) => sum + (c.documentCount || 0), 0)}
                  </Badge>
                </button>

                {/* Domains with their categories */}
                {domains.map((domain) => {
                  const domainCategories = categories.filter(c => c.domainId === domain.id);
                  const DomainIcon = getCategoryIcon(domain.icon || 'default');
                  return (
                    <div key={domain.id} className="mt-2">
                      <div className="flex items-center gap-1.5 px-2 py-1.5 text-[11px] font-semibold text-muted-foreground uppercase tracking-wider">
                        <DomainIcon className="h-3.5 w-3.5" />
                        <span className="flex-1 truncate">{domain.name}</span>
                        {isAdmin && (
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                              <Button variant="ghost" size="icon" className="h-5 w-5 opacity-0 group-hover:opacity-100 hover:opacity-100 focus:opacity-100">
                                <MoreHorizontal className="h-3.5 w-3.5" />
                              </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end">
                              <DropdownMenuItem onClick={() => openEditDomainDialog(domain)}>
                                <Pencil className="mr-2 h-4 w-4" /> Edit Domain
                              </DropdownMenuItem>
                              <DropdownMenuItem onClick={() => handleDeleteDomain(domain)} className="text-destructive">
                                <Trash2 className="mr-2 h-4 w-4" /> Delete Domain
                              </DropdownMenuItem>
                            </DropdownMenuContent>
                          </DropdownMenu>
                        )}
                      </div>
                      {domainCategories.length === 0 ? (
                        <p className="text-xs text-muted-foreground/60 px-3 py-1 italic">No categories</p>
                      ) : (
                        domainCategories.map((category) => {
                          const CategoryIcon = getCategoryIcon(category.slug);
                          return (
                            <div
                              key={category.id}
                              className={`group flex items-center gap-2 px-3 py-1.5 rounded-md text-sm transition-colors ml-1 ${
                                selectedCategory === category.id
                                  ? 'bg-primary text-primary-foreground'
                                  : 'hover:bg-muted'
                              }`}
                            >
                              <button
                                onClick={() => { setSelectedCategory(category.id); setCurrentPage(1); }}
                                className="flex-1 flex items-center gap-2 text-left"
                              >
                                <CategoryIcon className="h-4 w-4" />
                                <span className="truncate">{category.name}</span>
                              </button>
                              <Badge variant="secondary" className="text-xs">
                                {category.documentCount || 0}
                              </Badge>
                              {isAdmin && (
                                <DropdownMenu>
                                  <DropdownMenuTrigger asChild>
                                    <Button variant="ghost" size="icon" className={`h-6 w-6 opacity-0 group-hover:opacity-100 ${
                                      selectedCategory === category.id ? 'text-primary-foreground hover:bg-primary-foreground/20 opacity-100' : ''
                                    }`}>
                                      <MoreHorizontal className="h-4 w-4" />
                                    </Button>
                                  </DropdownMenuTrigger>
                                  <DropdownMenuContent align="end">
                                    <DropdownMenuItem onClick={() => openEditCategoryDialog(category)}>
                                      <Pencil className="mr-2 h-4 w-4" /> Edit
                                    </DropdownMenuItem>
                                    <DropdownMenuItem onClick={() => handleDeleteCategory(category)} className="text-destructive">
                                      <Trash2 className="mr-2 h-4 w-4" /> Delete
                                    </DropdownMenuItem>
                                  </DropdownMenuContent>
                                </DropdownMenu>
                              )}
                            </div>
                          );
                        })
                      )}
                    </div>
                  );
                })}

                {/* Uncategorized (categories with no domain) */}
                {(() => {
                  const ungrouped = categories.filter(c => !c.domainId);
                  if (ungrouped.length === 0) return null;
                  return (
                    <div className="mt-2">
                      <div className="flex items-center gap-1.5 px-2 py-1.5 text-[11px] font-semibold text-muted-foreground uppercase tracking-wider">
                        <FolderOpen className="h-3.5 w-3.5" />
                        <span className="flex-1">Uncategorized</span>
                      </div>
                      {ungrouped.map((category) => {
                        const CategoryIcon = getCategoryIcon(category.slug);
                        return (
                          <div
                            key={category.id}
                            className={`group flex items-center gap-2 px-3 py-1.5 rounded-md text-sm transition-colors ml-1 ${
                              selectedCategory === category.id
                                ? 'bg-primary text-primary-foreground'
                                : 'hover:bg-muted'
                            }`}
                          >
                            <button
                              onClick={() => { setSelectedCategory(category.id); setCurrentPage(1); }}
                              className="flex-1 flex items-center gap-2 text-left"
                            >
                              <CategoryIcon className="h-4 w-4" />
                              <span className="truncate">{category.name}</span>
                            </button>
                            <Badge variant="secondary" className="text-xs">
                              {category.documentCount || 0}
                            </Badge>
                            {isAdmin && (
                              <DropdownMenu>
                                <DropdownMenuTrigger asChild>
                                  <Button variant="ghost" size="icon" className={`h-6 w-6 opacity-0 group-hover:opacity-100 ${
                                    selectedCategory === category.id ? 'text-primary-foreground hover:bg-primary-foreground/20 opacity-100' : ''
                                  }`}>
                                    <MoreHorizontal className="h-4 w-4" />
                                  </Button>
                                </DropdownMenuTrigger>
                                <DropdownMenuContent align="end">
                                  <DropdownMenuItem onClick={() => openEditCategoryDialog(category)}>
                                    <Pencil className="mr-2 h-4 w-4" /> Edit
                                  </DropdownMenuItem>
                                  <DropdownMenuItem onClick={() => handleDeleteCategory(category)} className="text-destructive">
                                    <Trash2 className="mr-2 h-4 w-4" /> Delete
                                  </DropdownMenuItem>
                                </DropdownMenuContent>
                              </DropdownMenu>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  );
                })()}
              </div>
            )}
          </div>
        </Card>

        {/* Documents Panel */}
        <div className="flex flex-col gap-4 min-h-0">
          {/* Upload Section - Only show for admins when a category is selected */}
          {isAdmin && selectedCategory && (
            <Card className="border-dashed">
              <CardContent className="p-4">
                <div className="flex flex-col lg:flex-row gap-4">
                  {/* Dropzone */}
                  <div
                    {...getRootProps()}
                    className={`
                      flex-1 border-2 border-dashed rounded-lg p-4 text-center cursor-pointer transition-colors min-h-[100px] flex flex-col items-center justify-center
                      ${isDragActive ? 'border-primary bg-primary/5' : 'border-muted-foreground/25 hover:border-primary/50'}
                    `}
                  >
                    <input {...getInputProps()} />
                    <Upload className="h-8 w-8 text-muted-foreground mb-2" />
                    <p className="text-sm text-muted-foreground">
                      {isDragActive ? 'Drop files here...' : `Upload to "${selectedCategoryData?.name}"`}
                    </p>
                    <p className="text-xs text-muted-foreground mt-1">
                      PDF, DOC, TXT, CSV, XLS (max 50MB)
                    </p>
                  </div>

                  {/* Upload Button */}
                  <div className="flex flex-col gap-3 lg:w-[180px]">
                    <Button
                      onClick={uploadFiles}
                      disabled={pendingCount === 0 || isUploading}
                      className="w-full"
                    >
                      {isUploading ? (
                        <>
                          <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                          Uploading...
                        </>
                      ) : (
                        <>
                          <Upload className="mr-2 h-4 w-4" />
                          Upload {pendingCount > 0 ? `(${pendingCount})` : ''}
                        </>
                      )}
                    </Button>
                  </div>
                </div>

                {/* File List */}
                {files.length > 0 && (
                  <div className="mt-3 space-y-1.5 max-h-32 overflow-y-auto">
                    {files.map((uploadFile, index) => (
                      <div
                        key={index}
                        className="flex items-center gap-2 p-2 rounded-lg bg-muted/50"
                      >
                        <FileText className="h-4 w-4 text-muted-foreground flex-shrink-0" />
                        <div className="flex-1 min-w-0">
                          <p className="text-sm truncate">{uploadFile.file.name}</p>
                          {(uploadFile.status === 'uploading' || uploadFile.status === 'confirming' || uploadFile.status === 'processing') && (
                            <Progress value={uploadFile.progress} className="h-1 mt-1" />
                          )}
                          {uploadFile.status === 'error' && (
                            <p className="text-xs text-destructive truncate">{uploadFile.error}</p>
                          )}
                        </div>
                        <div className="flex-shrink-0">
                          {uploadFile.status === 'pending' && (
                            <Button variant="ghost" size="icon" className="h-6 w-6" onClick={() => removeFile(index)}>
                              <X className="h-3 w-3" />
                            </Button>
                          )}
                          {(uploadFile.status === 'uploading' || uploadFile.status === 'confirming' || uploadFile.status === 'processing') && (
                            <Loader2 className="h-4 w-4 animate-spin text-primary" />
                          )}
                          {uploadFile.status === 'success' && (
                            <CheckCircle2 className="h-4 w-4 text-green-500" />
                          )}
                          {uploadFile.status === 'error' && (
                            <AlertCircle className="h-4 w-4 text-destructive" />
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          )}

          {/* Search and Documents Table */}
          <Card className="flex-1 flex flex-col overflow-hidden min-h-0">
            <div className="p-4 border-b flex-shrink-0">
              <form onSubmit={handleSearch} className="flex gap-2">
                <div className="relative flex-1">
                  <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                  <Input
                    placeholder="Search global documents..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="pl-9"
                  />
                </div>
                <Button type="submit" variant="secondary">
                  Search
                </Button>
                {isAdmin && totalDocuments > 0 && (
                  <Button
                    variant="destructive"
                    onClick={() => setDeleteAllDialogOpen(true)}
                  >
                    <Trash2 className="mr-2 h-4 w-4" />
                    Delete All
                  </Button>
                )}
              </form>
            </div>
            <div className="p-0 flex-1 overflow-y-auto">
              {loadingDocuments ? (
                <div className="p-4 space-y-3">
                  {[...Array(5)].map((_, i) => (
                    <Skeleton key={i} className="h-12 w-full" />
                  ))}
                </div>
              ) : documents.length === 0 ? (
                <div className="p-8 text-center">
                  <FileText className="mx-auto h-12 w-12 text-muted-foreground" />
                  <h3 className="mt-4 text-lg font-semibold">
                    {selectedCategory ? 'No documents in this category' : 'No documents yet'}
                  </h3>
                  <p className="mt-2 text-sm text-muted-foreground">
                    {selectedCategory
                      ? 'Upload documents to this category using the form above'
                      : 'Select a category and upload documents'
                    }
                  </p>
                </div>
              ) : (
                <>
                <div className="overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Name</TableHead>
                        <TableHead className="hidden sm:table-cell">Category</TableHead>
                        <TableHead className="hidden md:table-cell">Topic</TableHead>
                        <TableHead className="hidden lg:table-cell">Size</TableHead>
                        <TableHead>Status</TableHead>
                        <TableHead className="hidden xl:table-cell">Uploaded</TableHead>
                        <TableHead className="w-[50px]"></TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {documents.map((doc) => {
                        const FileIcon = getFileIcon(doc.fileType);
                        const category = categories.find((c) => c.id === doc.categoryId);
                        return (
                          <TableRow key={doc.id}>
                            <TableCell>
                              <div className="flex items-center gap-2">
                                <FileIcon className="h-4 w-4 text-muted-foreground flex-shrink-0" />
                                <span className="truncate max-w-[150px] sm:max-w-[250px]">
                                  {doc.originalFilename}
                                </span>
                              </div>
                            </TableCell>
                            <TableCell className="hidden sm:table-cell">
                              {category && (
                                <Badge variant="outline" className="text-xs">
                                  {category.name}
                                </Badge>
                              )}
                            </TableCell>
                            <TableCell className="hidden md:table-cell">
                              {doc.mainTopic ? (
                                <div className="flex flex-wrap gap-1 max-w-[280px]">
                                  <Badge
                                    variant="default"
                                    className="text-xs px-1.5 py-0 bg-primary text-primary-foreground font-medium"
                                  >
                                    {doc.mainTopic}
                                  </Badge>
                                  {doc.subTopics?.slice(0, 2).map((topic, idx) => (
                                    <Badge
                                      key={idx}
                                      variant="secondary"
                                      className="text-xs px-1.5 py-0"
                                    >
                                      {topic}
                                    </Badge>
                                  ))}
                                  {(doc.subTopics?.length || 0) > 2 && (
                                    <Popover>
                                      <PopoverTrigger asChild>
                                        <span className="inline-flex">
                                          <Badge variant="outline" className="text-xs px-1.5 py-0 cursor-pointer hover:bg-accent">
                                            +{(doc.subTopics?.length || 0) - 2}
                                          </Badge>
                                        </span>
                                      </PopoverTrigger>
                                      <PopoverContent side="top" className="w-auto p-3">
                                        <div className="space-y-2 max-w-[300px]">
                                          <div>
                                            <span className="text-xs text-muted-foreground font-medium">Main Topic:</span>
                                            <div className="mt-1">
                                              <Badge
                                                variant="default"
                                                className="text-xs bg-primary text-primary-foreground font-medium"
                                              >
                                                {doc.mainTopic}
                                              </Badge>
                                            </div>
                                          </div>
                                          {(doc.subTopics?.length || 0) > 0 && (
                                            <div>
                                              <span className="text-xs text-muted-foreground font-medium">
                                                Sub-topics ({doc.subTopics?.length}):
                                              </span>
                                              <div className="flex flex-wrap gap-1 mt-1">
                                                {doc.subTopics?.map((topic, idx) => (
                                                  <Badge
                                                    key={idx}
                                                    variant="secondary"
                                                    className="text-xs"
                                                  >
                                                    {topic}
                                                  </Badge>
                                                ))}
                                              </div>
                                            </div>
                                          )}
                                        </div>
                                      </PopoverContent>
                                    </Popover>
                                  )}
                                </div>
                              ) : (
                                <span className="text-xs text-muted-foreground">—</span>
                              )}
                            </TableCell>
                            <TableCell className="hidden lg:table-cell">
                              {formatFileSize(doc.fileSizeBytes)}
                            </TableCell>
                            <TableCell>
                              <StatusBadge status={reindexingDocId === doc.id ? 'indexing' : doc.status} />
                            </TableCell>
                            <TableCell className="hidden xl:table-cell text-muted-foreground text-sm">
                              {formatDistanceToNow(new Date(doc.createdAt), { addSuffix: true })}
                            </TableCell>
                            <TableCell>
                              <DropdownMenu>
                                <DropdownMenuTrigger asChild>
                                  <Button variant="ghost" size="icon" className="h-8 w-8">
                                    <MoreHorizontal className="h-4 w-4" />
                                  </Button>
                                </DropdownMenuTrigger>
                                <DropdownMenuContent align="end">
                                  <DropdownMenuItem onClick={() => handleDownload(doc)}>
                                    <Download className="mr-2 h-4 w-4" />
                                    Download
                                  </DropdownMenuItem>
                                  {isAdmin && (
                                    <>
                                      <DropdownMenuItem onClick={() => handleViewChunks(doc)}>
                                        <List className="mr-2 h-4 w-4" />
                                        View Chunks
                                      </DropdownMenuItem>
                                      <DropdownMenuItem
                                        onClick={() => handleReindex(doc)}
                                        disabled={reindexingDocId !== null}
                                      >
                                        {reindexingDocId === doc.id ? (
                                          <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                                        ) : (
                                          <RefreshCw className="mr-2 h-4 w-4" />
                                        )}
                                        {reindexingDocId === doc.id
                                          ? 'Indexing...'
                                          : doc.status === 'indexed'
                                            ? 'Re-index'
                                            : 'Index File'}
                                      </DropdownMenuItem>
                                      <DropdownMenuItem
                                        onClick={() => handleDeleteDocument(doc)}
                                        className="text-destructive"
                                      >
                                        <Trash2 className="mr-2 h-4 w-4" />
                                        Delete
                                      </DropdownMenuItem>
                                    </>
                                  )}
                                </DropdownMenuContent>
                              </DropdownMenu>
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </div>
                {/* Pagination */}
                {totalPages > 1 && (
                  <div className="flex items-center justify-between px-4 py-3 border-t">
                    <span className="text-sm text-muted-foreground">
                      {totalDocuments} document{totalDocuments !== 1 ? 's' : ''} — Page {currentPage} of {totalPages}
                    </span>
                    <div className="flex items-center gap-1">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                        disabled={currentPage <= 1}
                      >
                        <ChevronLeft className="h-4 w-4" />
                      </Button>
                      {Array.from({ length: totalPages }, (_, i) => i + 1)
                        .filter((page) => {
                          if (totalPages <= 7) return true;
                          if (page === 1 || page === totalPages) return true;
                          if (Math.abs(page - currentPage) <= 1) return true;
                          return false;
                        })
                        .reduce<(number | string)[]>((acc, page, idx, arr) => {
                          if (idx > 0 && page - (arr[idx - 1] as number) > 1) {
                            acc.push('...');
                          }
                          acc.push(page);
                          return acc;
                        }, [])
                        .map((item, idx) =>
                          typeof item === 'string' ? (
                            <span key={`ellipsis-${idx}`} className="px-2 text-sm text-muted-foreground">...</span>
                          ) : (
                            <Button
                              key={item}
                              variant={currentPage === item ? 'default' : 'outline'}
                              size="sm"
                              className="min-w-[32px]"
                              onClick={() => setCurrentPage(item)}
                            >
                              {item}
                            </Button>
                          )
                        )}
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
                        disabled={currentPage >= totalPages}
                      >
                        <ChevronRight className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                )}
                </>
              )}
            </div>
          </Card>
        </div>
      </div>

      {/* Category Dialog */}
      <Dialog open={categoryDialogOpen} onOpenChange={setCategoryDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {editingCategory ? 'Edit Category' : 'Create New Category'}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Domain</Label>
              <Select
                value={categoryForm.domainId || '_none'}
                onValueChange={(v) => setCategoryForm({ ...categoryForm, domainId: v === '_none' ? '' : v })}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select a domain..." />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="_none">No Domain</SelectItem>
                  {domains.map((domain) => (
                    <SelectItem key={domain.id} value={domain.id}>{domain.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label htmlFor="name">Name</Label>
              <Input
                id="name"
                value={categoryForm.name}
                onChange={(e) => setCategoryForm({ ...categoryForm, name: e.target.value })}
                placeholder="e.g., Accounting"
              />
            </div>
            {!editingCategory && (
              <div className="space-y-2">
                <Label htmlFor="slug">Slug</Label>
                <Input
                  id="slug"
                  value={categoryForm.slug}
                  onChange={(e) => setCategoryForm({ ...categoryForm, slug: e.target.value })}
                  placeholder="e.g., accounting (auto-generated if empty)"
                />
                <p className="text-xs text-muted-foreground">
                  URL-friendly identifier. Leave empty to auto-generate from name.
                </p>
              </div>
            )}
            <div className="space-y-2">
              <Label htmlFor="description">Description</Label>
              <Textarea
                id="description"
                value={categoryForm.description}
                onChange={(e) => setCategoryForm({ ...categoryForm, description: e.target.value })}
                placeholder="Brief description of this category"
                rows={3}
              />
            </div>
            <div className="space-y-2">
              <Label>Icon</Label>
              <Select
                value={categoryForm.icon}
                onValueChange={(v) => setCategoryForm({ ...categoryForm, icon: v })}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="default">
                    <div className="flex items-center gap-2">
                      <FolderOpen className="h-4 w-4" />
                      Default
                    </div>
                  </SelectItem>
                  <SelectItem value="accounting">
                    <div className="flex items-center gap-2">
                      <Calculator className="h-4 w-4" />
                      Accounting
                    </div>
                  </SelectItem>
                  <SelectItem value="tax">
                    <div className="flex items-center gap-2">
                      <Landmark className="h-4 w-4" />
                      Tax
                    </div>
                  </SelectItem>
                  <SelectItem value="legal">
                    <div className="flex items-center gap-2">
                      <Scale className="h-4 w-4" />
                      Legal
                    </div>
                  </SelectItem>
                  <SelectItem value="engineering">
                    <div className="flex items-center gap-2">
                      <Cog className="h-4 w-4" />
                      Engineering
                    </div>
                  </SelectItem>
                  <SelectItem value="hr">
                    <div className="flex items-center gap-2">
                      <Users className="h-4 w-4" />
                      HR
                    </div>
                  </SelectItem>
                  <SelectItem value="finance">
                    <div className="flex items-center gap-2">
                      <Briefcase className="h-4 w-4" />
                      Finance
                    </div>
                  </SelectItem>
                  <SelectItem value="marketing">
                    <div className="flex items-center gap-2">
                      <Globe className="h-4 w-4" />
                      Marketing
                    </div>
                  </SelectItem>
                  <SelectItem value="health">
                    <div className="flex items-center gap-2">
                      <Heart className="h-4 w-4" />
                      Health
                    </div>
                  </SelectItem>
                  <SelectItem value="education">
                    <div className="flex items-center gap-2">
                      <GraduationCap className="h-4 w-4" />
                      Education
                    </div>
                  </SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={closeCategoryDialog}>
              Cancel
            </Button>
            <Button
              onClick={handleCategorySubmit}
              disabled={!categoryForm.name || createCategoryMutation.isPending || updateCategoryMutation.isPending}
            >
              {(createCategoryMutation.isPending || updateCategoryMutation.isPending) && (
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              )}
              {editingCategory ? 'Update' : 'Create'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Document Dialog */}
      <AlertDialog open={deleteDocDialogOpen} onOpenChange={setDeleteDocDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Document</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete "{selectedDocument?.originalFilename}"? This action
              cannot be undone and will also remove the document from the knowledge base.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => selectedDocument && deleteDocMutation.mutate(selectedDocument.id)}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {deleteDocMutation.isPending ? (
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              ) : (
                <Trash2 className="mr-2 h-4 w-4" />
              )}
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Delete Category Dialog */}
      <AlertDialog open={deleteCategoryDialogOpen} onOpenChange={setDeleteCategoryDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Category</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete the "{selectedCategoryToDelete?.name}" category?
              {(selectedCategoryToDelete?.documentCount || 0) > 0 && (
                <span className="block mt-2 text-destructive font-medium">
                  Warning: This category contains {selectedCategoryToDelete?.documentCount} document(s) that will also be deleted.
                </span>
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => selectedCategoryToDelete && deleteCategoryMutation.mutate(selectedCategoryToDelete.id)}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {deleteCategoryMutation.isPending ? (
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              ) : (
                <Trash2 className="mr-2 h-4 w-4" />
              )}
              Delete Category
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Domain Dialog */}
      <Dialog open={domainDialogOpen} onOpenChange={setDomainDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{editingDomain ? 'Edit Domain' : 'Create New Domain'}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="domain-name">Name</Label>
              <Input
                id="domain-name"
                value={domainForm.name}
                onChange={(e) => setDomainForm({ ...domainForm, name: e.target.value })}
                placeholder="e.g., Accounting & Finance"
              />
            </div>
            {!editingDomain && (
              <div className="space-y-2">
                <Label htmlFor="domain-slug">Slug</Label>
                <Input
                  id="domain-slug"
                  value={domainForm.slug}
                  onChange={(e) => setDomainForm({ ...domainForm, slug: e.target.value })}
                  placeholder="e.g., accounting-finance (auto-generated if empty)"
                />
                <p className="text-xs text-muted-foreground">
                  URL-friendly identifier. Leave empty to auto-generate from name.
                </p>
              </div>
            )}
            <div className="space-y-2">
              <Label htmlFor="domain-description">Description</Label>
              <Textarea
                id="domain-description"
                value={domainForm.description}
                onChange={(e) => setDomainForm({ ...domainForm, description: e.target.value })}
                placeholder="Brief description of this domain"
                rows={3}
              />
            </div>
            <div className="space-y-2">
              <Label>Icon</Label>
              <Select value={domainForm.icon} onValueChange={(v) => setDomainForm({ ...domainForm, icon: v })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="default"><div className="flex items-center gap-2"><FolderOpen className="h-4 w-4" /> Default</div></SelectItem>
                  <SelectItem value="accounting"><div className="flex items-center gap-2"><Calculator className="h-4 w-4" /> Accounting</div></SelectItem>
                  <SelectItem value="tax"><div className="flex items-center gap-2"><Landmark className="h-4 w-4" /> Tax</div></SelectItem>
                  <SelectItem value="legal"><div className="flex items-center gap-2"><Scale className="h-4 w-4" /> Legal</div></SelectItem>
                  <SelectItem value="engineering"><div className="flex items-center gap-2"><Cog className="h-4 w-4" /> Engineering</div></SelectItem>
                  <SelectItem value="hr"><div className="flex items-center gap-2"><Users className="h-4 w-4" /> HR</div></SelectItem>
                  <SelectItem value="finance"><div className="flex items-center gap-2"><Briefcase className="h-4 w-4" /> Finance</div></SelectItem>
                  <SelectItem value="marketing"><div className="flex items-center gap-2"><Globe className="h-4 w-4" /> Marketing</div></SelectItem>
                  <SelectItem value="health"><div className="flex items-center gap-2"><Heart className="h-4 w-4" /> Health</div></SelectItem>
                  <SelectItem value="education"><div className="flex items-center gap-2"><GraduationCap className="h-4 w-4" /> Education</div></SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={closeDomainDialog}>Cancel</Button>
            <Button
              onClick={handleDomainSubmit}
              disabled={!domainForm.name || createDomainMutation.isPending || updateDomainMutation.isPending}
            >
              {(createDomainMutation.isPending || updateDomainMutation.isPending) && (
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              )}
              {editingDomain ? 'Update' : 'Create'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Domain Dialog */}
      <AlertDialog open={deleteDomainDialogOpen} onOpenChange={setDeleteDomainDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Domain</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete the "{selectedDomainToDelete?.name}" domain?
              <span className="block mt-2 text-muted-foreground">
                Categories in this domain will not be deleted — they will become uncategorized.
              </span>
              {(selectedDomainToDelete?.categoryCount || 0) > 0 && (
                <span className="block mt-1 text-amber-600 dark:text-amber-400 font-medium">
                  This domain contains {selectedDomainToDelete?.categoryCount} category(ies) that will be unassigned.
                </span>
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => selectedDomainToDelete && deleteDomainMutation.mutate(selectedDomainToDelete.id)}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {deleteDomainMutation.isPending ? (
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              ) : (
                <Trash2 className="mr-2 h-4 w-4" />
              )}
              Delete Domain
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Delete All Documents Dialog */}
      <AlertDialog open={deleteAllDialogOpen} onOpenChange={setDeleteAllDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete All Documents</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete <strong>ALL {totalDocuments} documents</strong>
              {selectedCategory ? ` in "${selectedCategoryData?.name}"` : ' across all categories'}?
              <span className="block mt-2 text-destructive font-medium">
                This will permanently delete all files from storage and remove all vector embeddings.
                This action cannot be undone.
              </span>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => deleteAllMutation.mutate(selectedCategory || undefined)}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {deleteAllMutation.isPending ? (
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              ) : (
                <Trash2 className="mr-2 h-4 w-4" />
              )}
              Delete All Documents
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Upload Dialog */}
      <Dialog open={uploadDialogOpen} onOpenChange={(v) => !v && closeUploadDialog()}>
        <DialogContent className="max-w-2xl max-h-[85vh] flex flex-col p-0 gap-0 [&>button]:hidden">
          <DialogHeader className="px-6 py-4 border-b shrink-0">
            <div className="flex items-center justify-between">
              <DialogTitle className="text-lg font-semibold">Upload Documents</DialogTitle>
              <Button variant="ghost" size="sm" className="h-8 w-8 p-0" onClick={closeUploadDialog} disabled={isDialogUploading}>
                <X className="h-4 w-4" />
              </Button>
            </div>
          </DialogHeader>

          <div className="flex-1 overflow-auto px-6 py-4 space-y-4">
            {/* Category selector */}
            <div className="space-y-1.5">
              <Label className="text-sm font-medium">Category</Label>
              <Select value={uploadCategoryId} onValueChange={setUploadCategoryId} disabled={isDialogUploading}>
                <SelectTrigger className="w-full">
                  <SelectValue placeholder="Select a category..." />
                </SelectTrigger>
                <SelectContent>
                  {categories.map((cat) => (
                    <SelectItem key={cat.id} value={cat.id}>
                      {cat.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Drop zone */}
            <div
              className="border-2 border-dashed rounded-lg p-8 text-center cursor-pointer hover:border-primary/50 hover:bg-muted/30 transition-colors"
              onClick={() => {
                const input = document.getElementById('upload-dialog-input') as HTMLInputElement;
                input?.click();
              }}
              onDrop={(e) => { e.preventDefault(); e.stopPropagation(); handleDialogFileSelect(e.dataTransfer.files); }}
              onDragOver={(e) => { e.preventDefault(); e.stopPropagation(); }}
            >
              <Upload className="h-8 w-8 mx-auto mb-2 text-muted-foreground" />
              <p className="text-sm font-medium">Drop files here or click to browse</p>
              <p className="text-xs text-muted-foreground mt-1">
                PDF, DOC, TXT, CSV, XLS (max 50MB)
              </p>
              <input
                id="upload-dialog-input"
                type="file"
                multiple
                className="hidden"
                accept=".pdf,.txt,.csv,.docx,.doc,.xlsx,.xls"
                onChange={(e) => { handleDialogFileSelect(e.target.files); e.target.value = ''; }}
              />
            </div>

            {/* File list */}
            {dialogFiles.length > 0 && (
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <p className="text-sm font-medium">
                    {dialogFiles.length} file{dialogFiles.length !== 1 ? 's' : ''}
                    {dialogDoneCount > 0 && ` (${dialogDoneCount} completed)`}
                  </p>
                  {dialogDoneCount > 0 && !isDialogUploading && (
                    <Button variant="ghost" size="sm" onClick={() => setDialogFiles((prev) => prev.filter((f) => f.status !== 'success'))}>
                      Clear completed
                    </Button>
                  )}
                </div>

                <div className="border rounded-lg divide-y max-h-[300px] overflow-auto">
                  {dialogFiles.map((uf, index) => (
                    <div key={index} className="flex items-center gap-3 px-3 py-2">
                      <div className="shrink-0">
                        {uf.status === 'pending' && <FileText className="h-4 w-4 text-muted-foreground" />}
                        {uf.status === 'uploading' && <Loader2 className="h-4 w-4 text-blue-500 animate-spin" />}
                        {uf.status === 'confirming' && <Loader2 className="h-4 w-4 text-orange-500 animate-spin" />}
                        {uf.status === 'processing' && <Loader2 className="h-4 w-4 text-purple-500 animate-spin" />}
                        {uf.status === 'success' && <CheckCircle2 className="h-4 w-4 text-green-500" />}
                        {uf.status === 'error' && <XCircle className="h-4 w-4 text-destructive" />}
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm truncate">{uf.file.name}</p>
                        <div className="flex items-center gap-2">
                          <span className="text-xs text-muted-foreground">
                            {formatFileSize(uf.file.size)}
                          </span>
                          {uf.status === 'uploading' && <span className="text-xs text-blue-500">Uploading...</span>}
                          {uf.status === 'confirming' && <span className="text-xs text-orange-500">Confirming...</span>}
                          {uf.status === 'processing' && <span className="text-xs text-purple-500">Indexing...</span>}
                          {uf.error && <span className="text-xs text-destructive truncate">{uf.error}</span>}
                        </div>
                        {(uf.status === 'uploading' || uf.status === 'confirming' || uf.status === 'processing') && (
                          <Progress value={uf.progress} className="h-1 mt-1" />
                        )}
                      </div>
                      {uf.status === 'pending' && !isDialogUploading && (
                        <Button variant="ghost" size="sm" className="h-6 w-6 p-0 shrink-0" onClick={() => removeDialogFile(index)}>
                          <X className="h-3 w-3" />
                        </Button>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Footer */}
          <div className="px-6 py-3 border-t shrink-0 flex items-center justify-between bg-muted/30">
            <div className="text-xs text-muted-foreground">
              {!uploadCategoryId && dialogFiles.length > 0 && (
                <span className="flex items-center gap-1 text-amber-600">
                  <AlertCircle className="h-3 w-3" /> Select a category first
                </span>
              )}
            </div>
            <div className="flex items-center gap-2">
              {isDialogUploading ? (
                <Button variant="destructive" size="sm" onClick={() => setIsDialogUploading(false)}>
                  Stop
                </Button>
              ) : (
                <>
                  <Button variant="outline" size="sm" onClick={closeUploadDialog}>
                    Cancel
                  </Button>
                  <Button
                    size="sm"
                    onClick={uploadDialogFiles}
                    disabled={!uploadCategoryId || dialogPendingCount === 0}
                  >
                    <Upload className="h-3.5 w-3.5 mr-1.5" />
                    Upload {dialogPendingCount > 0 ? `${dialogPendingCount} file${dialogPendingCount !== 1 ? 's' : ''}` : ''}
                  </Button>
                </>
              )}
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* View Chunks Dialog */}
      <Dialog open={viewChunksDialogOpen} onOpenChange={setViewChunksDialogOpen}>
        <DialogContent className="max-w-3xl max-h-[85vh] flex flex-col">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <List className="h-5 w-5" />
              Document Chunks
            </DialogTitle>
            {viewChunksDoc && (
              <p className="text-sm text-muted-foreground truncate">
                {viewChunksDoc.originalFilename}
                {chunksData && ` — ${chunksData.totalChunks} chunks`}
              </p>
            )}
          </DialogHeader>
          <div className="overflow-y-auto max-h-[60vh] px-4 py-4">
            {loadingChunks ? (
              <div className="flex items-center justify-center py-12">
                <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
              </div>
            ) : chunksData && chunksData.chunks.length > 0 ? (
              <div className="space-y-4">
                {chunksData.chunks.map((chunk) => (
                  <div key={chunk.index} className="border rounded-lg p-4">
                    <div className="flex items-center justify-between mb-2">
                      <Badge variant="outline" className="text-xs">
                        Chunk {chunk.index + 1}
                      </Badge>
                      {chunk.tokens && (
                        <span className="text-xs text-muted-foreground">
                          {chunk.tokens} tokens
                        </span>
                      )}
                    </div>
                    <ChunkContent text={chunk.text} />
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-12 text-muted-foreground">
                <FileText className="h-12 w-12 mx-auto mb-4 opacity-50" />
                <p>No chunks found for this document</p>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setViewChunksDialogOpen(false)}>
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
